import { useEffect } from 'react'
import type { Language } from '../config/siteContent'
import { getRouteSeo } from '../config/seoV2'
import { siteConfig } from '../config/site'

type PageMeta = {
  language: Language
  title: string
  description: string
}

export function usePageMeta({ language, title, description }: PageMeta) {
  useEffect(() => {
    const seo = getRouteSeo(window.location.pathname)
    const baseUrl = (siteConfig.siteUrl || window.location.origin).replace(/\/$/, '')
    const absoluteUrl = (path: string) => `${baseUrl}${path}`
    const ensureMeta = (selector: string, attribute: 'name' | 'property', key: string, value: string) => {
      let element = document.querySelector<HTMLMetaElement>(selector)
      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attribute, key)
        document.head.append(element)
      }
      element.content = value
    }
    const ensureLink = (hreflang: string | null, rel: string, href: string) => {
      const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`
      let element = document.querySelector<HTMLLinkElement>(selector)
      if (!element) {
        element = document.createElement('link')
        element.rel = rel
        if (hreflang) element.hreflang = hreflang
        document.head.append(element)
      }
      element.href = href
    }

    document.documentElement.lang = seo.language
    document.title = seo.title

    ensureMeta('meta[name="description"]', 'name', 'description', seo.description || description)
    ensureMeta('meta[name="robots"]', 'name', 'robots', seo.indexable ? 'index, follow' : 'noindex, follow')
    ensureMeta('meta[property="og:title"]', 'property', 'og:title', seo.title)
    ensureMeta('meta[property="og:description"]', 'property', 'og:description', seo.description)
    ensureMeta('meta[property="og:url"]', 'property', 'og:url', absoluteUrl(seo.path))
    ensureMeta('meta[property="og:image"]', 'property', 'og:image', absoluteUrl(seo.socialImage))
    ensureMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', seo.socialImageAlt)
    ensureMeta('meta[property="og:locale"]', 'property', 'og:locale', seo.language === 'el' ? 'el_GR' : 'en_US')
    ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title)
    ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description)
    ensureMeta('meta[name="twitter:image"]', 'name', 'twitter:image', absoluteUrl(seo.socialImage))
    ensureMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', seo.socialImageAlt)
    ensureLink(null, 'canonical', absoluteUrl(seo.path))
    ensureLink('el', 'alternate', absoluteUrl(seo.alternatePaths.el))
    ensureLink('en', 'alternate', absoluteUrl(seo.alternatePaths.en))
    ensureLink('x-default', 'alternate', absoluteUrl(seo.alternatePaths.el))
  }, [description, language, title])
}
